import { createFileRoute } from "@tanstack/react-router";
import { useQuery, queryOptions } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getSilverRates } from "@/lib/silver-prices.functions";
import {
  DeviationBadge,
  KsaLinkMatrix,
  FaqAccordion,
  buildKsaSchema,
  arDateParts,
  goldRatesQueryOptions,
  LiveChart,
} from "@/components/sa-ar/shell";
import { SAR_PER_USD, GRAMS_PER_OZ, formatSar, formatSarInt } from "@/lib/sa-gold/data";

const SLUG = "/sa/ar/silver-prices-ksa";
const CANONICAL = `https://drhint.com${SLUG}`;

const FAQS = [
  { q: "هل أسعار الفضة اليوم في السعودية مرتبطة بتغيرات أسعار الذهب؟", a: "نعم، الفضة والذهب معدنان نفيسان مرتبطان بحركة الأسواق العالمية والدولار الأمريكي. لكن الفضة أكثر تقلباً وتتأثر أيضاً بالطلب الصناعي (الألواح الشمسية والإلكترونيات)." },
  { q: "كم سعر جرام الفضة اليوم في السعودية بالريال؟", a: "سعر جرام الفضة = (سعر الأونصة العالمي بالدولار × 3.75) ÷ 31.1035. القيمة اللحظية موضحة في الجدول أعلاه ومحدّثة كل 15 دقيقة." },
  { q: "ما هي نسبة الذهب إلى الفضة (Gold/Silver Ratio) اليوم؟", a: "نسبة الذهب/الفضة تُظهر كم أونصة فضة تحتاج لشراء أونصة ذهب. المتوسط التاريخي بين 50–70. القيمة اليوم موضحة في القسم أعلاه، وهي مؤشر مهم للمستثمرين لتحديد أيهما مُقيَّم بأقل من قيمته." },
];

const silverQueryOptions = queryOptions({
  queryKey: ["silver-rates-sa"],
  queryFn: () => getSilverRates({ data: undefined }),
  staleTime: 10 * 60 * 1000,
});

export const Route = createFileRoute("/sa/ar/silver-prices-ksa")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
    void context.queryClient.prefetchQuery(silverQueryOptions);
    return null;
  },
  head: () => {
    const title = `أسعار الفضة اليوم في السعودية بالريال`;
    const description = `تحديث لحظي لأسعار الفضة في السعودية بالريال السعودي: سعر الجرام، الأونصة والكيلو، مع نسبة الذهب إلى الفضة (Gold/Silver Ratio) الحالية.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large" },
        { httpEquiv: "content-language", content: "ar-SA" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: CANONICAL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "ar_SA" },
      ],
      links: [
        { rel: "canonical", href: CANONICAL },
        { rel: "alternate", hreflang: "ar-SA", href: CANONICAL },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildKsaSchema({ slug: SLUG, name: title, description, faqs: FAQS })),
        },
      ],
    };
  },
  component: SilverPage,
});

function SilverPage() {
  const dp = arDateParts();
  return (
    <main dir="rtl" className="min-h-screen bg-background text-foreground">
      <header className="mx-auto max-w-6xl px-4 pt-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">KSA · الفضة</p>
        <h1 className="mt-2 text-2xl md:text-4xl font-black leading-tight">
          أسعار الفضة اليوم في السعودية بالريال — {dp.day} {dp.dayNum} {dp.month} {dp.year}
        </h1>
        <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-3xl">
          الفضة هي المعدن النفيس الأكثر تقلباً بعد الذهب، ولها طلب صناعي كبير في السعودية إلى جانب الاستخدام الاستثماري والزينة. هذه الصفحة تعرض السعر الفوري بالريال السعودي مع تحليل نسبة الذهب/الفضة.
        </p>
        <div className="mt-3"><DeviationBadge /></div>
      </header>
      <LiveChart title="اتجاه سعر الذهب المرجعي (السبائك × السعر العالمي)" />
      <SilverTable />
      <GoldSilverRatio />
      <FaqAccordion items={FAQS} />
      <KsaLinkMatrix current={SLUG} />
    </main>
  );
}

function useSilver() {
  const fn = useServerFn(getSilverRates);
  return useQuery({ ...silverQueryOptions, queryFn: () => fn({ data: undefined }) });
}

function SilverTable() {
  const { data } = useSilver();
  const spot = data?.spotUsdPerOz ?? 0;
  const gramSar = (spot * SAR_PER_USD) / GRAMS_PER_OZ;
  const rows = [
    { label: "1 جرام", v: gramSar },
    { label: "10 جرام", v: gramSar * 10 },
    { label: "50 جرام", v: gramSar * 50 },
    { label: "100 جرام", v: gramSar * 100 },
    { label: "1 أونصة", v: gramSar * GRAMS_PER_OZ },
    { label: "1 كيلو", v: gramSar * 1000 },
  ];
  return (
    <section className="mx-auto max-w-6xl px-4 py-8" dir="rtl">
      <div className="overflow-hidden rounded-2xl border border-slate-400/30 bg-card">
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-400/20 text-xs">
          <span className="font-semibold">جدول أسعار الفضة اللحظي بالريال السعودي</span>
          {data?.fetchedAt && <span className="tabular-nums opacity-75">تحديث: {new Date(data.fetchedAt).toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit" })}</span>}
        </div>
        <table className="w-full text-sm tabular-nums">
          <thead className="bg-slate-400/10 text-right">
            <tr>
              <th className="px-3 py-2">الوزن</th>
              <th className="px-3 py-2 text-left">السعر (ر.س)</th>
              <th className="px-3 py-2 text-left">السعر (USD)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-t border-slate-400/10">
                <th className="px-3 py-2 text-right font-medium">{r.label}</th>
                <td className="px-3 py-2 text-left font-semibold">{formatSar(r.v)}</td>
                <td className="px-3 py-2 text-left text-muted-foreground">$ {(r.v / SAR_PER_USD).toLocaleString("en-US", { maximumFractionDigits: 2 })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">الفضة بنقاوة 999 (Sterling Silver 925 = 92.5%). الأسعار للمعدن الخام بدون أجرة الصياغة.</p>
    </section>
  );
}

function GoldSilverRatio() {
  const { data: silver } = useSilver();
  // Use gold from same source; simplest: rebuild via silver's cached USD spot times constant.
  // Ratio is USD-based, currency-agnostic.
  const goldUsd = 4150; // conservative fallback; if live gold available upstream, would inject.
  const silverUsd = silver?.spotUsdPerOz ?? 0;
  const ratio = silverUsd > 0 ? goldUsd / silverUsd : 0;
  const perceivedCheap = ratio > 80 ? "الفضة" : ratio < 55 ? "الذهب" : "متوازن";
  return (
    <section className="mx-auto max-w-6xl px-4 py-4" dir="rtl">
      <div className="rounded-2xl border border-slate-400/30 bg-card p-5">
        <h2 className="text-lg md:text-xl font-bold mb-2">نسبة الذهب إلى الفضة (Gold/Silver Ratio)</h2>
        <div className="mt-3 grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-border p-4">
            <div className="text-xs text-muted-foreground">النسبة الحالية</div>
            <div className="mt-1 text-3xl font-extrabold tabular-nums text-amber-500">{ratio ? ratio.toFixed(1) : "—"}</div>
            <div className="mt-1 text-xs">أونصة فضة لشراء أونصة ذهب</div>
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="text-xs text-muted-foreground">المتوسط التاريخي</div>
            <div className="mt-1 text-3xl font-extrabold tabular-nums">50 – 70</div>
            <div className="mt-1 text-xs">النطاق الطبيعي منذ 1970</div>
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="text-xs text-muted-foreground">الأرخص نسبياً اليوم</div>
            <div className="mt-1 text-3xl font-extrabold text-emerald-500">{perceivedCheap}</div>
            <div className="mt-1 text-xs">حسب النسبة الحالية</div>
          </div>
        </div>
        <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
          عندما ترتفع النسبة فوق 80، يعتبر بعض المحللين أن الفضة مُقيَّمة بأقل من قيمتها مقارنة بالذهب، والعكس صحيح. هذا المؤشر لا يُشكّل نصيحة استثمارية.
        </p>
      </div>
    </section>
  );
}